import { acceptHMRUpdate, defineStore } from 'pinia';
import { http as axios } from '@/services/http.service';

export interface Specialty {
  id?: number;
  name: string;
  duration_blocks: number;
  description?: string | null;
  price?: number;
  block_size_minutes?: number | null;
}

interface SpecialtiesState {
  items: Specialty[];
  loading: boolean;
  error: string;
}

export const useSpecialtiesStore = defineStore('specialties.store', {
  state: (): SpecialtiesState => ({ items: [], loading: false, error: '' }),
  actions: {
    async fetchAll(): Promise<Specialty[]> {
      this.loading = true;
      this.error = '';
      try {
        const res = await axios.get('/api/v1/specialties');
        this.items = Array.isArray(res.data) ? res.data : [];
        return this.items;
      } catch (e: unknown) {
        const message =
          typeof e === 'object' && e && 'message' in e
            ? String((e as { message?: unknown }).message)
            : '';
        this.error = message || 'Error cargando especialidades';
        this.items = [];
        return [];
      } finally {
        this.loading = false;
      }
    },
    async create(data: Specialty): Promise<Specialty | null> {
      const res = await axios.post('/api/v1/specialties', { specialty: data });
      const item = res.data as Specialty;
      this.items.push(item);
      return item;
    },
    async update(id: number, data: Partial<Specialty>): Promise<Specialty | null> {
      const res = await axios.put(`/api/v1/specialties/${id}`, { specialty: data });
      const updated = res.data as Specialty;
      const idx = this.items.findIndex((i) => i.id === id);
      if (idx !== -1) this.items[idx] = updated;
      return updated;
    },
    async remove(id: number): Promise<void> {
      await axios.delete(`/api/v1/specialties/${id}`);
      this.items = this.items.filter((i) => i.id !== id);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSpecialtiesStore, import.meta.hot));
}
